import React, { useState, useEffect, useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import DashboardMobileTabHeader from './DashboardMobileTabHeader';
import WebullStyleTabs from '../shared/WebullStyleTabs';
import MarketOverviewPage from './MarketOverviewPage';
import MarketRelativeStrengthPage from './MarketRelativeStrengthPage';
import CompanyFundamentals from '../../pages/CompanyFundamentals';
import { canAccessMarketDataTools } from '../../utils/brokerConnection';
import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import { MOBILE_BOTTOM_NAV_CLEARANCE } from '../../constants/mobileBottomNav';
import { MOBILE_UI } from '../../constants/mobileUi';

const MARKET_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'relative-strength', label: 'Relative Strength' },
  { id: 'research', label: 'Research' },
];

function tabFromPath(pathname) {
  if (pathname.includes('/market/relative-strength')) return 'relative-strength';
  if (pathname.includes('/market/research')) return 'research';
  return 'overview';
}

function routeForTab(tabId) {
  if (tabId === 'relative-strength') return DASHBOARD_ROUTES.marketRelativeStrength;
  if (tabId === 'research') return DASHBOARD_ROUTES.marketResearch;
  return DASHBOARD_ROUTES.marketOverview;
}

/**
 * Mobile Market tab.
 * Overview / Relative Strength / Research. Charts use live broker bars when any
 * linked connection has market data. Position charts live in the Portfolio cards.
 */
const MarketPage = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const canUseMarketTools = canAccessMarketDataTools(user, selectedAccount);

  const [activeTab, setActiveTab] = useState(() => tabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(routeForTab(tabId));
  };

  // Mobile Positions Overview moved into Portfolio positions cards — send deep links there.
  if (location.pathname.includes('/market/positions')) {
    return <Navigate to={DASHBOARD_ROUTES.overview} replace />;
  }

  if (!user) {
    return null;
  }

  if (!canUseMarketTools && activeTab !== 'research') {
    return <Navigate to={DASHBOARD_ROUTES.marketResearch} replace />;
  }

  return (
    <div
      data-testid="market-page"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.bgPrimary,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: MOBILE_BOTTOM_NAV_CLEARANCE,
        overflow: 'hidden',
      }}
    >
      <DashboardMobileTabHeader
        title="Market"
        menuCurrentPage="market"
        showAccountDropdown
      />

      {canUseMarketTools && (
        <div style={{ padding: `8px ${MOBILE_UI.pagePadding}px 0`, flexShrink: 0 }}>
          <WebullStyleTabs
            tabs={MARKET_TABS}
            activeTab={activeTab}
            onChange={handleTabChange}
            variant="filled"
          />
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {canUseMarketTools && activeTab === 'overview' && <MarketOverviewPage />}
        {canUseMarketTools && activeTab === 'relative-strength' && <MarketRelativeStrengthPage />}
        {activeTab === 'research' && (
          <div style={{ padding: MOBILE_UI.pagePadding }}>
            <CompanyFundamentals hideHeader />
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketPage;
